import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, ArrowRightLeft, History, ClipboardList } from 'lucide-react';
import { erpAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import { rows, errorText, Stat, Loading } from '../../utils/erpHelpers';
import './ERPManagement.css';

const InventoryManagement: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const closeToast = useCallback(() => setMessage(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, trnRes, movRes] = await Promise.all([
        erpAPI.list('inventory'),
        erpAPI.list('transfers'),
        erpAPI.list('stock-movements'),
      ]);
      setInventory(rows(invRes.data));
      setTransfers(rows(trnRes.data));
      setMovements(rows(movRes.data));
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cards = [
    {
      icon: <Boxes size={28} />,
      label: 'Stock Summary',
      desc: 'View current inventory balances, costs, and pricing at all locations.',
      path: '/admin/inventory/stock-summary',
      stat: `${inventory.length} stock records`,
      color: '#2563eb',
    },
    {
      icon: <ArrowRightLeft size={28} />,
      label: 'Manage Stocks',
      desc: 'Create, approve, dispatch, and receive transfers between locations.',
      path: '/admin/inventory/transfers',
      stat: `${transfers.filter(x => x.status !== 'RECEIVED' && x.status !== 'CANCELLED').length} active`,
      color: '#8b5cf6',
    },
    {
      icon: <History size={28} />,
      label: 'Movement History',
      desc: 'Immutable audit trail of every stock change across all locations.',
      path: '/admin/inventory/stock-movements',
      stat: `${movements.length} total movements`,
      color: '#0ea5e9',
    },
    {
      icon: <ClipboardList size={28} />,
      label: 'Stock Adjustment',
      desc: 'Create or correct stock records, update costs and reorder levels.',
      path: '/admin/inventory/stock-adjustment',
      stat: `${inventory.filter(x => Number(x.quantity || 0) <= Number(x.reorder_level || 0)).length} low-stock`,
      color: '#f59e0b',
    },
  ];

  if (loading) return <Loading />;

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={closeToast} />

      <div className="erp-heading">
        <div>
          <h1>Inventory Management</h1>
          <p>Manage stock, transfers, movements, and adjustments across all business locations.</p>
        </div>
      </div>

      <div className="erp-stats">
        <Stat label="Total stock records" value={inventory.length} />
        <Stat label="Total physical units" value={inventory.reduce((sum, row) => sum + Number(row.quantity || 0), 0)} />
        <Stat label="Available units" value={inventory.reduce((sum, row) => sum + Number(row.available_quantity || 0), 0)} />
        <Stat label="Locations" value={new Set(inventory.map(x => x.location_name)).size} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 8 }}>
        {cards.map(card => (
          <div
            key={card.path}
            onClick={() => navigate(card.path)}
            style={{
              background: 'var(--admin-card, #fff)',
              border: '1px solid var(--admin-border, #e2e8f0)',
              borderRadius: 12,
              padding: 20,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s, transform 0.15s',
              boxShadow: '0 1px 2px rgba(15,23,42,.04)',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,23,42,.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,.04)'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: `${card.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color }}>
                {card.icon}
              </div>
              <div>
                <strong style={{ fontSize: 16, display: 'block' }}>{card.label}</strong>
                <span style={{ fontSize: 12, color: '#64748b' }}>{card.stat}</span>
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, margin: 0 }}>{card.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InventoryManagement;
